import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill } from "../../components/common/Primitives";
import { Button } from "../../components/ui/button";
import api, { formatApiError } from "../../lib/api";
import { Coins, Loader2, Upload, Trash2, FileText } from "lucide-react";

const inr = (v) => "₹" + Number(v).toLocaleString("en-IN");

const STATUSES = ["pending_payment", "paid", "in_progress", "delivered", "cancelled"];

export default function AdminDaasOrders() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [saving, setSaving] = useState(null);
  const [uploading, setUploading] = useState(null);

  const load = () =>
    api
      .get(`/admin/daas/orders${filter !== "all" ? `?status=${filter}` : ""}`)
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const advance = async (order, next) => {
    setSaving(order.order_id);
    try {
      await api.patch(`/admin/daas/orders/${order.order_id}/status`, {
        status: next,
      });
      toast.success(`${order.order_id} → ${next}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  const uploadDeliverable = async (order, file) => {
    if (!file) return;
    setUploading(order.order_id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("note", "");
      await api.post(`/admin/daas/orders/${order.order_id}/deliverables`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(null);
    }
  };

  const deleteDeliverable = async (order, docId) => {
    if (!window.confirm("Delete this deliverable? The founder will lose access.")) return;
    try {
      await api.delete(`/admin/daas/orders/${order.order_id}/deliverables/${docId}`);
      toast.success("Deliverable removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <DashboardShell title="Diligence Orders" subtitle="ADMIN · DaaS QUEUE">
      <Toaster richColors position="top-right" />
      <div className="flex gap-2 mb-4 flex-wrap">
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`daas-filter-${s}`}
            className={
              "px-3 py-1.5 rounded-md text-xs uppercase font-mono tracking-wider " +
              (filter === s
                ? "bg-navy text-white"
                : "bg-white text-ink border border-navy/[0.1] hover:bg-navy/[0.05]")
            }
          >
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <Card testId="admin-daas-orders-card">
        {rows.length === 0 ? (
          <div className="text-ink-muted italic text-sm py-6 text-center">No orders in this bucket.</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-daas-orders-table">
            <thead>
              <tr className="text-[10px] font-mono uppercase text-ink-muted tracking-wider">
                <th className="text-left py-2">Order</th>
                <th className="text-left py-2">Startup</th>
                <th className="text-left py-2">Modules</th>
                <th className="text-right py-2">Amount</th>
                <th className="text-right py-2">Status</th>
                <th className="text-right py-2 pr-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr
                  key={o.order_id}
                  className="border-t border-navy/[0.06]"
                  data-testid={`admin-daas-row-${o.order_id}`}
                >
                  <td className="py-3 font-mono text-xs">{o.order_id}</td>
                  <td className="py-3">{o.startup_name || o.startup_id}</td>
                  <td className="py-3 text-xs text-ink-muted max-w-[220px] truncate">
                    {(o.module_ids || []).map((m) => m.replace(/_/g, " ")).join(", ")}
                  </td>
                  <td className="py-3 text-right font-mono">{inr(o.total_inr)}</td>
                  <td className="py-3 text-right">
                    <Pill
                      tone={
                        o.status === "delivered"
                          ? "success"
                          : o.status === "cancelled"
                          ? "danger"
                          : o.status === "paid" || o.status === "in_progress"
                          ? "gold"
                          : "muted"
                      }
                    >
                      {o.status.replace(/_/g, " ")}
                    </Pill>
                  </td>
                  <td className="py-3 pr-2 text-right">
                    <div className="flex flex-col gap-1 items-end">
                      {o.status !== "delivered" && o.status !== "cancelled" && (
                        <select
                          onChange={(e) => advance(o, e.target.value)}
                          disabled={saving === o.order_id}
                          value=""
                          className="text-xs border border-navy/20 rounded px-1 py-1"
                          data-testid={`admin-daas-advance-${o.order_id}`}
                        >
                          <option value="" disabled>Advance…</option>
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                          ))}
                        </select>
                      )}
                      <label
                        className={
                          "inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded cursor-pointer border border-navy/20 hover:bg-navy/[0.05] " +
                          (uploading === o.order_id ? "opacity-50 pointer-events-none" : "")
                        }
                        data-testid={`admin-daas-upload-${o.order_id}`}
                      >
                        {uploading === o.order_id ? (
                          <Loader2 className="animate-spin" size={10} />
                        ) : (
                          <Upload size={10} />
                        )}
                        Upload deliverable
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.txt"
                          onChange={(e) => uploadDeliverable(o, e.target.files?.[0])}
                        />
                      </label>
                      {(o.deliverables || []).map((d) => (
                        <div
                          key={d.doc_id}
                          className="flex items-center gap-1 text-[10px] font-mono text-ink-muted"
                          data-testid={`admin-daas-deliverable-${d.doc_id}`}
                        >
                          <FileText size={10} />
                          <span title={d.filename}>{d.filename.length > 18 ? d.filename.slice(0, 15) + "…" : d.filename}</span>
                          <button
                            onClick={() => deleteDeliverable(o, d.doc_id)}
                            className="text-rose-500 hover:text-rose-700"
                            data-testid={`admin-daas-deliverable-delete-${d.doc_id}`}
                          >
                            <Trash2 size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </DashboardShell>
  );
}
